import { PUZZLE_PENDING_LIKES_KEY } from "@/constants/puzzle-storage.constants";
import {
    COMMENT_LIKE_TARGET_TYPE,
    PLAYABLE_PROPERTY_LIKE_TARGET_TYPE,
} from "@/constants/like.constants";
import { likeComment, likePlayableProperty, type LikeTargetType } from "@/services/LikeService";

export type PendingLikeIntent = {
    targetType: LikeTargetType;
    targetId: string;
};

const MAX_PENDING_LIKES = 20;

let memoryPendingLikes: PendingLikeIntent[] = [];

function intentKey(intent: PendingLikeIntent): string {
    return `${intent.targetType}:${intent.targetId}`;
}

function isValidIntent(value: unknown): value is PendingLikeIntent {
    if (!value || typeof value !== "object") {
        return false;
    }
    const row = value as Record<string, unknown>;
    const targetType = row.targetType;
    const targetId = row.targetId;
    if (typeof targetId !== "string" || !targetId.trim()) {
        return false;
    }
    return (
        targetType === PLAYABLE_PROPERTY_LIKE_TARGET_TYPE || targetType === COMMENT_LIKE_TARGET_TYPE
    );
}

function readQueueFromSessionStorage(): PendingLikeIntent[] {
    try {
        const raw = sessionStorage.getItem(PUZZLE_PENDING_LIKES_KEY);
        if (!raw) {
            return [];
        }
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed.filter(isValidIntent);
    } catch {
        return [];
    }
}

function readQueue(): PendingLikeIntent[] {
    const fromSession = readQueueFromSessionStorage();
    if (fromSession.length > 0) {
        return fromSession;
    }
    return memoryPendingLikes.filter(isValidIntent);
}

function writeQueue(queue: PendingLikeIntent[]): void {
    const trimmed = queue.slice(-MAX_PENDING_LIKES);
    memoryPendingLikes = trimmed;
    try {
        if (trimmed.length === 0) {
            sessionStorage.removeItem(PUZZLE_PENDING_LIKES_KEY);
            return;
        }
        sessionStorage.setItem(PUZZLE_PENDING_LIKES_KEY, JSON.stringify(trimmed));
    } catch {
        /* sessionStorage unavailable; in-memory queue only */
    }
}

/** Whether any guest like intents are waiting to be replayed after sign-in. */
export function hasPendingLikes(): boolean {
    return readQueue().length > 0;
}

/** Queues a like intent for replay after the user completes sign-in. */
export function queuePendingLike(intent: PendingLikeIntent): void {
    const targetId = String(intent.targetId).trim();
    if (!targetId) {
        return;
    }
    const normalized: PendingLikeIntent = {
        targetType: intent.targetType,
        targetId,
    };
    const key = intentKey(normalized);
    const withoutDuplicate = readQueue().filter((item) => intentKey(item) !== key);
    withoutDuplicate.push(normalized);
    writeQueue(withoutDuplicate);
}

/** Clears all queued like intents. */
export function clearPendingLikes(): void {
    memoryPendingLikes = [];
    try {
        sessionStorage.removeItem(PUZZLE_PENDING_LIKES_KEY);
    } catch {
        /* ignore */
    }
}

/**
 * Replays queued like intents for a registered user. Failed items remain queued.
 *
 * @returns Number of likes successfully applied.
 */
export async function replayPendingLikes(userId: string): Promise<number> {
    const resolvedUserId = String(userId).trim();
    if (!resolvedUserId) {
        return 0;
    }
    const queue = readQueue();
    if (queue.length === 0) {
        return 0;
    }
    let replayed = 0;
    const remaining: PendingLikeIntent[] = [];
    for (const intent of queue) {
        try {
            if (intent.targetType === PLAYABLE_PROPERTY_LIKE_TARGET_TYPE) {
                await likePlayableProperty(intent.targetId, resolvedUserId);
            } else {
                await likeComment(intent.targetId, resolvedUserId);
            }
            replayed += 1;
        } catch {
            remaining.push(intent);
        }
    }
    writeQueue(remaining);
    return replayed;
}
